import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { WarnLevels } from '../Constants/constants';
import _ from 'lodash';

import getStateManager from '../StateManager.js';
import { createNestedSettingsDraft, createSettingsDraftStore, retainSettingsReadback, settingsLeafPaths,
  persistSettingsDraft as writeTempSettings, getSettingsRestorePreference } from '../Utils/settings_draft';
import { useReceivedSettings, useTabController } from '../Hooks/useTabController';

export { writeTempSettings };

/*
 * @param parent Can be either a controller or a zone if controller suppors multi zone
 */
const stateManager = getStateManager();
// Existing tabs use nested drafts and the normal StateManager save. Specialized
// forms can supply a draft adapter, editable baseline, and submission callback
// while sharing the controls lifecycle. onChange observes display changes only;
// persistence always goes through the draft adapter.
export function settingControls(controller, parent, tab_id, values, setValues, options = {}) {
  let currentValues = values;
  const baseline = () => (options.getBaseline ? options.getBaseline() : parent.settings[tab_id]);
  const draft =
    options.draft ??
    createNestedSettingsDraft(
      createSettingsDraftStore(parent, tab_id, () => writeTempSettings(parent))
    );
  const writeValues = (next, change) => {
    options.onChange?.(change);
    currentValues = next;
    setValues(next);
  };
  const canEdit = () => options.canEdit?.() ?? true;
  const entriesFor = (paths, nextValues, roundRoot = false) =>
    (Array.isArray(paths)
      ? paths.map((path, index) => [path, nextValues[index]])
      : [[paths, nextValues]]
    )
      .filter(([path]) => options.isEditablePath?.(path) ?? true)
      .map(([path, value]) => [
        path,
        options.normalizeValue
          ? options.normalizeValue(path, value)
          : roundRoot && path === null
            ? Math.round(value)
            : value
      ]);
  const controls = {
    retainSetting: options.retainSetting,
    saveTempSettings: (paths, nextValues) => {
      if (!canEdit()) return;
      const entries = entriesFor(paths, nextValues);
      if (entries.length) draft.edit(entries);
    },
    setValue: (paths, nextValues, saveTemp = true) => {
      if (!canEdit()) return;
      const entries = entriesFor(paths, nextValues, true);
      if (!entries.length) return;
      entries.forEach(([path]) => options.retainSetting?.(path));
      let next = _.cloneDeep(currentValues);
      entries.forEach(([path, value]) => {
        if (path === null) next = _.cloneDeep(value);
        else _.set(next, path, _.cloneDeep(value));
      });
      if (saveTemp) draft.edit(entries);
      writeValues(next, { type: 'edit', entries, saveTemp });
    },
    resetSetting: (path) => {
      const next = _.cloneDeep(currentValues);
      const value = _.cloneDeep(_.get(baseline(), path));
      _.set(next, path, value);
      draft.remove([path]);
      writeValues(next, { type: 'reset', entries: [[path, value]] });
    },
    resetSettings: () => {
      draft.clear();
      writeValues(_.cloneDeep(baseline()), { type: 'resetAll' });
    },
    restoreTempSettings: () => {
      if (options.canRestore?.() === false) return;
      writeValues(draft.restore(baseline()), { type: 'restore' });
    },
    hasChanged: (path) => !_.isEqual(_.get(currentValues, path), _.get(baseline(), path)),
    saveSettings: (transform = null, doTransform = false) => {
      if (options.submit) {
        if (!options.submit()) return false;
      } else {
        if (typeof transform !== 'function') transform = null;
        if (typeof doTransform !== 'boolean') doTransform = false;
        const sent = doTransform
          ? stateManager.saveSettings(controller, parent, transform(currentValues), tab_id, null)
          : stateManager.saveSettings(controller, parent, currentValues, tab_id, transform);
        if (!sent) return false;
      }
      draft.clear();
      return true;
    },
    addDeletedArrayIndex: (index, path = '') => {
      const deleted = _.cloneDeep(draft.read()._deleted_indicies ?? {});
      (deleted[path] ??= []).push(index);
      controls.saveTempSettings('_deleted_indicies', deleted);
    },
    setValueMinMaxSlider: (minPath, maxPath, min, max, saveTemp = true) =>
      controls.setValue([minPath, maxPath], [min, max], saveTemp),
    resetMinMaxSlider: (minPath, maxPath) => {
      const entries = [minPath, maxPath].map((path) => [
        path,
        _.cloneDeep(_.get(baseline(), path))
      ]);
      const next = _.cloneDeep(currentValues);
      entries.forEach(([path, value]) => _.set(next, path, value));
      draft.remove([minPath, maxPath]);
      writeValues(next, { type: 'reset', entries });
    }
  };
  return controls;
}

const unchangedSettings = (values) => values;

// Reuse each form's draft adapter. Widgets register the fields they edit, so
// readback can retain those values without freezing undisplayed payload fields.
export function useSettingsReadback(parent, category, received, values, setValues, options = {}) {
  const paths = useRef(new Map());
  const previews = useRef(new Map());
  const previous = useRef(received);
  const nestedDraft = useMemo(() => createNestedSettingsDraft(
    createSettingsDraftStore(parent, category, () => writeTempSettings(parent))
  ), [parent, category]);
  // A draft awaiting the existing Restore/Discard choice is not the displayed
  // form. Readback must not overwrite it with the initial on-screen values.
  // Feature migration effects run before this effect, but after render. Read
  // the initial draft here only once those migrations have populated it.
  const awaitingRestore = useRef(null);
  const latest = useRef();
  latest.current = { values, setValues, options, draft: options.draft ?? nestedDraft };
  const canRetain = options.canRetain !== false;
  const previousCanRetain = useRef(canRetain);
  useEffect(() => {
    const { values, setValues, options, draft } = latest.current;
    if (awaitingRestore.current === null) {
      const initialDraft = draft.read();
      awaitingRestore.current = !_.isEmpty(initialDraft) && !_.isEqual(initialDraft, received);
    }
    const before = previous.current;
    previous.current = received;
    const regainedWrite = canRetain && !previousCanRetain.current;
    previousCanRetain.current = canRetain;
    if (options.enabled === false) return;
    if (!canRetain) previews.current.clear();
    if (before === received && canRetain && !regainedWrite) return;
    options.beforeReadback?.();
    const retainedPaths = canRetain && !awaitingRestore.current ? [...paths.current.values()] : [];
    // Read-only values are controller readback, not the suspended editor. Resume
    // only its already-active committed draft, never a pending recovery choice.
    // Registered paths keep removed physical records out of the displayed form.
    const resumeDraft = regainedWrite && !awaitingRestore.current;
    const currentValues = resumeDraft ? draft.restore(received) : values;
    const result = retainSettingsReadback(currentValues, resumeDraft ? received : before,
      received, retainedPaths);
    // A collection draft may contain a previewed child. Persist committed values
    // even when the registered path is an ancestor of that slider's path.
    const committedValues = previews.current.size ? _.cloneDeep(currentValues) : currentValues;
    previews.current.forEach((value, key) => _.set(committedValues, JSON.parse(key), _.cloneDeep(value)));
    const edits = [];
    const confirmed = [];
    for (const [path] of result.changed) {
      const committed = _.get(committedValues, path);
      const incoming = _.get(received, path);
      const matches = options.readbackMatches
        ? options.readbackMatches(path, committed, incoming) : _.isEqual(committed, incoming);
      if (matches) confirmed.push(path);
      else edits.push([path, committed]);
    }
    if (confirmed.length) draft.remove(confirmed);
    if (edits.length) draft.edit(edits);
    // Clock editors own their live presentation and in-progress date/time edits.
    for (const path of options.preservePaths ?? []) {
      if (_.has(values, path)) _.set(result.values, path, _.cloneDeep(_.get(values, path)));
    }
    if (!_.isEqual(values, result.values)) setValues(result.values);
  }, [received, canRetain]);
  const retainSetting = (path) => {
    if (path != null && !(options.preservePaths ?? []).includes(path))
      paths.current.set(JSON.stringify(_.toPath(path)), path);
  };
  return {
    canRestore: () => canRetain,
    retainSetting,
    onChange: ({ type, entries = [], saveTemp }) => {
      awaitingRestore.current = false;
      if (type === 'resetAll' || type === 'restore') previews.current.clear();
      if (type === 'restore') settingsLeafPaths(latest.current.draft.read()).forEach(retainSetting);
      for (const [path] of entries) {
        const key = JSON.stringify(_.toPath(path));
        if (type === 'edit' && !saveTemp) {
          if (!previews.current.has(key)) previews.current.set(key, _.cloneDeep(_.get(latest.current.values, path)));
        } else previews.current.delete(key);
      }
    }
  };
}

export const useTabSettings = (
  tab_id = null,
  exclude_from_effect = [],
  normalizeSettings = unchangedSettings,
  additionalDraft = null,
  draft = null
) => {
  let { t } = useTranslation();
  let params = useParams();
  let stateManager = getStateManager();
  const { controller, parent, controllerSnapshot, parentSnapshot } = useTabController(params);
  let settings = parentSnapshot.settings;
  let t_id = tab_id ?? params.tab_id;
  const [storedValues, setStoredValues] = useState(() =>
    normalizeSettings(_.cloneDeep(settings[t_id]))
  );
  // Normalize both reads and writes so restored drafts and retained form state
  // cannot reintroduce fields from an incompatible settings format.
  const values = normalizeSettings(storedValues);
  const setValues = useCallback(
    (next) => {
      setStoredValues((previous) =>
        normalizeSettings(typeof next === 'function' ? next(normalizeSettings(previous)) : next)
      );
    },
    [normalizeSettings]
  );
  const received = useReceivedSettings(parent, t_id);
  const readback = useSettingsReadback(parent, t_id, received, values, setValues, {
    draft,
    canRetain: controllerSnapshot.access_rights?.[t_id]?.write === true,
    preservePaths: exclude_from_effect === 'all' ? Object.keys(values ?? {}) : exclude_from_effect
  });
  const controls = settingControls(controller, parent, t_id, values, setValues, { ...readback, draft });
  const restoreControls = additionalDraft
    ? {
        restoreTempSettings: () => {
          controls.restoreTempSettings();
          additionalDraft.controls.current?.restoreTempSettings();
        },
        resetSettings: () => {
          controls.resetSettings();
          additionalDraft.controls.current?.reset();
        }
      }
    : controls;
  const differs = additionalDraft
    ? () =>
        (!_.isEmpty(parent.temp_settings[t_id]) &&
          !_.isEqual(parent.settings[t_id], parent.temp_settings[t_id])) ||
        additionalDraft.differs(controller)
    : null;

  // Restore after the initial baseline effect so automatic draft loading wins.
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(
    parent,
    restoreControls,
    t_id,
    differs,
    additionalDraft?.ready?.(controller)
  );

  return {
    t: t,
    params: params,
    stateManager: stateManager,
    controller: controller,
    settings: settings,
    parent: parent,
    controllerSnapshot: controllerSnapshot,
    parentSnapshot: parentSnapshot,
    values: values,
    setValues: setValues,
    controls: controls,
    restoreControls,
    openLoadSettingsDialog: openLoadSettingsDialog,
    closeLoadSettingsDialog: closeLoadSettingsDialog,
    tab_id: t_id
  };
};

export const useSavedSettings = (
  parent,
  controls,
  sid = null,
  custom_differ = null,
  readinessKey = null
) => {
  let { t } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();
  let { tab_id } = useParams();
  tab_id = sid ?? tab_id;
  const [open_load_settings_dialog, setOpenLoadSettingsDialog] = useState(false);

  const differ = custom_differ
    ? custom_differ
    : () => {
        return (
          !_.isEmpty(parent.temp_settings[tab_id]) &&
          !_.isEqual(parent.settings[tab_id], parent.temp_settings[tab_id])
        );
      };

  const form = useRef();
  form.current = { differ, controls, enqueueSnackbar, t };
  const departure = useRef({ differ, enqueueSnackbar, t, tab_id });
  useEffect(() => {
    const { differ, controls, enqueueSnackbar, t } = form.current;
    const differing = differ();

    const auto_load = getSettingsRestorePreference();
    setOpenLoadSettingsDialog(differing && !auto_load);
    // Apply the existing preference when this form or an additional draft becomes ready.
    if (differing) {
      // Auto apply saved settings.
      if (auto_load === '1') {
        controls.restoreTempSettings();
        enqueueSnackbar(t('auto_saved_loaded'), { variant: 'info' });
      }
      // Auto delete saved settings.
      else if (auto_load === '0') {
        controls.resetSettings();
        enqueueSnackbar(t('auto_saved_deleted'), { variant: 'info' });
      }
    }
  }, [readinessKey]);

  useEffect(() => {
    // Warning only when leaving the tab, not when an additional draft becomes ready.
    return () => {
      const { differ, enqueueSnackbar, t, tab_id } = departure.current;
      if (differ()) {
        enqueueSnackbar(t('auto_save_leaving_page_warn') + ' ' + t(tab_id), {
          variant: WarnLevels['unsaved_settings']
        });
      }
    };
  }, []);

  return {
    openLoadSettingsDialog: open_load_settings_dialog,
    closeLoadSettingsDialog: () => {
      setOpenLoadSettingsDialog(false);
    }
  };
};
